import type { SupportedUiLocale } from './locales';

export type DisplayDateValue = Date | number;

const dateTimeFormatFieldNames = new Set([
  'dateStyle',
  'timeStyle',
  'weekday',
  'era',
  'year',
  'month',
  'day',
  'dayPeriod',
  'hour',
  'minute',
  'second',
  'fractionalSecondDigits',
  'timeZoneName',
  'formatMatcher',
  'hour12',
  'hourCycle',
]);

const hasDateTimeFormatFields = (options: Intl.DateTimeFormatOptions): boolean => (
  Object.keys(options).some((optionName) => dateTimeFormatFieldNames.has(optionName))
);

export const formatDisplayDate = (
  value: DisplayDateValue,
  locale: SupportedUiLocale,
  options: Intl.DateTimeFormatOptions = {},
): string => new Intl.DateTimeFormat(locale, hasDateTimeFormatFields(options)
  ? options
  : { dateStyle: 'medium', ...options }).format(value);

export const formatDisplayDateTime = (
  value: DisplayDateValue,
  locale: SupportedUiLocale,
  options: Intl.DateTimeFormatOptions = {},
): string => new Intl.DateTimeFormat(locale, hasDateTimeFormatFields(options)
  ? options
  : { dateStyle: 'medium', timeStyle: 'short', ...options }).format(value);

export const formatDisplayNumber = (
  value: number,
  locale: SupportedUiLocale,
  options: Intl.NumberFormatOptions = {},
): string => new Intl.NumberFormat(locale, options).format(value);

export const formatDisplayPercent = (
  value: number,
  locale: SupportedUiLocale,
  options: Intl.NumberFormatOptions = {},
): string => new Intl.NumberFormat(locale, {
  style: 'percent',
  ...options,
}).format(value);

export const formatDisplayCurrency = (
  value: number,
  locale: SupportedUiLocale,
  currency: string,
  options: Intl.NumberFormatOptions = {},
): string => new Intl.NumberFormat(locale, {
  style: 'currency',
  currency,
  ...options,
}).format(value);

export const formatDisplayUnit = (
  value: number,
  locale: SupportedUiLocale,
  unit: Intl.NumberFormatOptions['unit'],
  options: Intl.NumberFormatOptions = {},
): string => new Intl.NumberFormat(locale, {
  style: 'unit',
  unit,
  ...options,
}).format(value);

export const formatRelativeTime = (
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  locale: SupportedUiLocale,
  options: Intl.RelativeTimeFormatOptions = {},
): string => new Intl.RelativeTimeFormat(locale, {
  numeric: 'auto',
  ...options,
}).format(value, unit);

export const createLocaleCollator = (
  locale: SupportedUiLocale,
  options: Intl.CollatorOptions = {},
): Intl.Collator => new Intl.Collator(locale, options);

export const createLocaleFormatters = (locale: SupportedUiLocale) => ({
  formatDate: (value: DisplayDateValue, options?: Intl.DateTimeFormatOptions) => (
    formatDisplayDate(value, locale, options)
  ),
  formatDateTime: (value: DisplayDateValue, options?: Intl.DateTimeFormatOptions) => (
    formatDisplayDateTime(value, locale, options)
  ),
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => (
    formatDisplayNumber(value, locale, options)
  ),
  formatPercent: (value: number, options?: Intl.NumberFormatOptions) => (
    formatDisplayPercent(value, locale, options)
  ),
  formatCurrency: (
    value: number,
    currency: string,
    options?: Intl.NumberFormatOptions,
  ) => formatDisplayCurrency(value, locale, currency, options),
  formatUnit: (
    value: number,
    unit: Intl.NumberFormatOptions['unit'],
    options?: Intl.NumberFormatOptions,
  ) => formatDisplayUnit(value, locale, unit, options),
  formatRelativeTime: (
    value: number,
    unit: Intl.RelativeTimeFormatUnit,
    options?: Intl.RelativeTimeFormatOptions,
  ) => formatRelativeTime(value, unit, locale, options),
  createCollator: (options?: Intl.CollatorOptions) => createLocaleCollator(locale, options),
});

/** Keeps API, storage, and filename date values independent of the UI locale. */
export const formatIsoDate = (value: Date): string => value.toISOString().slice(0, 10);